import React from 'react';
import { OptionWithDescription, OptionWithDescriptionType, Select } from '~/components/Select';
import { ServicesContext } from '~/services';
import { enums } from '~/util';
import { EntityTypeType } from '~/components/Admin';

export function useRoleOptions() {
    const context = React.useContext(ServicesContext);

    const roleOptions: OptionWithDescriptionType[] = [
        {
            value: enums(['organization_roles', 'ROLE_ORG_ADMIN', 'key']),
            label: enums(['organization_roles', 'ROLE_ORG_ADMIN', 'name']),
            description:
                'A role that provides write access to all gap types plus the option to manage users',
        },
        {
            value: enums(['organization_roles', 'ROLE_ORG_USER', 'key']),
            label: enums(['organization_roles', 'ROLE_ORG_USER', 'name']),
            description: 'A role that provides write access to all gap types',
        },
        {
            value: enums(['organization_roles', 'ROLE_ORG_SUPPORTER', 'key']),
            label: enums(['organization_roles', 'ROLE_ORG_SUPPORTER', 'name']),
            description:
                'A role that provides write access to all gap types and ignores EMR access configurations for TINs. For eAttestations, attachments are always required and EMR references are hidden',
        },
        {
            value: enums(['organization_roles', 'ROLE_ORG_CONTRIBUTOR', 'key']),
            label: enums(['organization_roles', 'ROLE_ORG_CONTRIBUTOR', 'name']),
            description: context.user.hasFeatureToggle('hidden_foc')
                ? 'A role that provides write access to Quality gaps'
                : 'A role that provides write access to AW and Quality gaps',
        },
        {
            value: enums(['organization_roles', 'ROLE_ORG_VIEWER', 'key']),
            label: enums(['organization_roles', 'ROLE_ORG_VIEWER', 'name']),
            description: 'A role that prevents write access to any gap type',
        },
    ];

    function defaultRole(entityType: EntityTypeType) {
        return entityType === 'market' ? roleOptions[4] : roleOptions[1];
    }

    function filterRolesByEntityType(entityType: EntityTypeType) {
        switch (entityType) {
            case 'macl':
                return [roleOptions[1], roleOptions[3], roleOptions[4]];
            case 'market':
                return [roleOptions[2], roleOptions[3], roleOptions[4]];
            default:
                return roleOptions;
        }
    }

    return { roleOptions, filterRolesByEntityType, defaultRole };
}

interface RoleSelectionProps {
    setRole: (x: OptionWithDescriptionType) => void;
    value: undefined | OptionWithDescriptionType;
    entityName: string | number;
    entityType: EntityTypeType;
}
export function RoleSelection({ setRole, value, entityName, entityType }: RoleSelectionProps) {
    const { filterRolesByEntityType } = useRoleOptions();
    return (
        <Select
            menuPosition="fixed"
            components={{ Option: OptionWithDescription }}
            classNamePrefix="option-with-description"
            label={`${entityName} Role`}
            // MACL does not get the admin option nor supporter option
            options={filterRolesByEntityType(entityType)}
            value={value}
            onChange={(val: OptionWithDescriptionType) => setRole(val)}
        />
    );
}
